import Block from '@components/Layout/Block';
import { Minus, Plus } from '@components/MiscComponents';
import { FC, useEffect, useState } from 'react';

import {
  DrawerHeader,
  DrawerSubheader,
  Drawers,
  FlexDivContent,
  FlexDivFourColumn,
  FlexDivHeder,
  FlexDrawer,
  FlexTermDrawer,
  FlexTermsContent,
  FlexTermsHeader,
  Item,
  Section,
  SectionList,
  Sections,
  TaxDrawer,
} from './styles';

import {
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Table,
  TableCell,
  TableRow,
} from '@mui/material';
import { useCurrency } from '@util/customHooks/useCurrency';
import { useTranslations } from 'next-intl';
import { DrawerItem } from './mapData';
import { getTestId } from '@util/getTestIds';

interface DrawerProps {
  drawers: DrawerItem[];
  baseCurrency?: string;
  currencyCode?: string;
}

const PropertyDetailDrawer: FC<DrawerProps> = ({ drawers, currencyCode, baseCurrency }) => {
  const t = useTranslations();
  const { convertCurrency, formatCurrency } = useCurrency();
  const [taxData, setTaxData] = useState();

  useEffect(() => {
    const taxDrawer = drawers.find((drawer) => (drawer as any).type === 'Taxes');
    if (taxDrawer && currencyCode && baseCurrency) {
      populateTaxWithConversion(taxDrawer);
    }
  }, [currencyCode, drawers, baseCurrency]);

  const [accordionState, setAccordionState] = useState<Record<number, boolean>>(
    Object.fromEntries(drawers.map((_, i) => [i, false]) ?? []),
  );

  const handleItemClick = (index: number) => {
    setAccordionState((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const populateTaxWithConversion = async (taxDrawer) => {
    const { municipal, annual } = taxDrawer.taxes;

    const municipalModified = await convertTaxToCurrency(
      baseCurrency,
      currencyCode,
      municipal.data ?? [],
    );
    const annualModified = await convertTaxToCurrency(
      baseCurrency,
      currencyCode,
      annual.data ?? [],
    );

    setTaxData({
      municipal: { ...municipal, data: municipalModified },
      annual: { ...annual, data: annualModified },
    } as any);
  };

  const convertTaxToCurrency = async (baseCurrency, targetCurrency, taxData) => {
    const arr: any[] = [];
    for (const tax of taxData) {
      if (['Year'].includes(tax.type)) {
        arr.push({ ...tax });
        continue;
      }
      const convertedPrice = convertCurrency(tax.value, baseCurrency);
      arr.push({ ...tax, value: formatCurrency(convertedPrice, targetCurrency) });
    }
    return arr;
  };

  const renderSections = (sections) => {
    const subSectionDimensions = sections?.[0]?.dimensions || [];

    if (subSectionDimensions.length > 0) {
      return (
        <Table style={{ borderBottom: 0 }}>
          {sections.map((section, sectionIndex) => {
            return section.dimensions.map((subsection, index) => {
              const key = Object.keys(subsection)[0]!;
              return (
                <>
                  {index === 0 && sectionIndex !== 0 ? <br /> : <></>}
                  <TableRow
                    style={{
                      color: '#757575',
                    }}
                  >
                    <TableCell style={{ borderBottom: 0, paddingBottom: '5px' }}>
                      {index === 0 ? section.subheader : ''}
                    </TableCell>
                    <TableCell style={{ borderBottom: 0, paddingBottom: '5px' }}>{key}</TableCell>
                    <TableCell style={{ borderBottom: 0, paddingBottom: '5px' }}>
                      {subsection[key]}
                    </TableCell>
                  </TableRow>
                </>
              );
            });
          })}
        </Table>
      );
    }
    return (
      <Sections>
        {sections?.map((section, index) => (
          <Section data-testid={`${getTestId(section.subheader)}-section`} key={index.toString()}>
            {section.subheader && (
              <DrawerSubheader data-testid={`${getTestId(section.subheader)}-subheader`}>
                {t(section.subheader)}
              </DrawerSubheader>
            )}

            {renderSectionContent(section)}

            {/* {section.columns && section.columns.length > 0 ? (
              <SectionColumns>
                {section.columns.map((column, index) => (
                  <SectionColumnItem key={index.toString()}>
                    <SectionList>
                      {column.map((item, index) => (
                        <li key={index.toString()}>
                          {item && t(item)}
                        </li>
                      ))}
                    </SectionList>
                  </SectionColumnItem>
                ))}
              </SectionColumns>
            ) : (section.list && section.list.length > 0) ||
              (section.listColumned && section.listColumned.length > 0) ? (
              section.listColumned && section.listColumned.length > 0 ? (
                <SectionListNoColumn>
                  {section.listColumned.map((item, index) => (
                    <li key={index.toString()}>
                      {item && t(item)}
                    </li>
                  ))}
                </SectionListNoColumn>
              ) : (
                <SectionList>
                  {section.list.map((item, index) => (
                    <li key={index.toString()}>
                      {item && t(item)}
                    </li>
                  ))}
                </SectionList>
              )
            ) : null} */}
          </Section>
        ))}
      </Sections>
    );
  };

  const renderSectionContent = (section) => {
    let items = section.columns ?? section.listColumned ?? section.list ?? [];
    items = items.flatMap((item) => item);
    return (
      <FlexDrawer data-testid={`${getTestId(section.subheader)}-content`}>
        {items.map((item, idx) => {
          return (
            <FlexDivFourColumn
              data-testid={`${getTestId(item.split(':')[0])}-item`}
              key={`${item}-${idx}`}
              style={{ marginBottom: '0px' }}
            >
              <div style={{ paddingLeft: '24px' }}>
                <Item>{t(item)}</Item>
              </div>
            </FlexDivFourColumn>
          );
        })}
      </FlexDrawer>
    );
  };

  const renderFourColumnDrawer = (drawer) => {
    const { arr } = drawer;
    if (arr?.length > 0) {
      return (
        <FlexDrawer data-testid={`${getTestId(drawer.header)}-content`}>
          {arr.map((ele, idx) => {
            return (
              <FlexDivFourColumn key={`${ele}-${idx}`}>
                <FlexDivHeder data-testid={`${getTestId(ele.header)}-header`}>
                  {t(ele.header)}
                </FlexDivHeder>
                <FlexDivContent data-testid={`${getTestId(ele.header)}-content`}>
                  {t(ele.content)}
                </FlexDivContent>
              </FlexDivFourColumn>
            );
          })}
        </FlexDrawer>
      );
    }

    return <></>;
  };

  const renderTaxes = (taxes) => {
    const { municipal, annual } = taxes;
    let JSX = (
      <TaxDrawer>
        {municipal && municipal.data?.length > 0 && (
          <div data-testid="municipal-tax-section" className="tax-block">
            <div data-testid="municipal-tax-header" className="header">
              {municipal.name}
            </div>
            <div data-testid="municpal-tax-content" className="content">
              {municipal?.data?.map((tax, idx) => {
                return (
                  <div data-testid={`municipal-item-${idx}`} className="item">
                    <span className="name">{tax.name}</span>
                    <span className={`value ${tax.type === 'Total' ? 'total' : ''}`}>
                      {tax.value?.toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {annual && annual.data?.length > 0 && (
          <div data-testid="annual-tax-section" className="tax-block">
            <div data-testid="annual-tax-header" className="header">
              {annual.name}
            </div>
            <div data-testid="annual-tax-content" className="content">
              {annual?.data?.map((tax, idx) => {
                return (
                  <div data-testid={`annual-item-${idx}`} className="item">
                    <span className="name">{tax.name}</span>
                    <span className={`value ${tax.type === 'Total' ? 'total' : ''}`}>
                      {tax.value?.toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </TaxDrawer>
    );
    return JSX;
  };

  const renderAdditionalTerms = (drawer) => {
    return drawer?.arr?.map((ele, ind) => {
      return (
        <FlexTermDrawer
          data-testid="additional-terms-item"
          $isLastTerm={ind === drawer?.arr?.length - 1}
        >
          <FlexTermsHeader data-testid="additional-terms-header">{t(ele.header)}</FlexTermsHeader>
          <FlexTermsContent data-testid="additional-terms-content">{ele.content}</FlexTermsContent>
        </FlexTermDrawer>
      );
    });
  };

  return (
    <Block narrow>
      <DrawerHeader data-testid="property-details-header">{t('Property Details')}</DrawerHeader>
      <Drawers>
        <List>
          {drawers.map((drawer, index) => (
            <ListItem data-testid={`${getTestId(drawer.header)}-item`} key={index}>
              <ListItemButton disableRipple onClick={() => handleItemClick(index)}>
                <ListItemText primary={drawer.header} style={{ fontSize: '24px !important' }} />
                {accordionState[index] ? <Minus /> : <Plus />}
              </ListItemButton>

              {drawer && (
                <Collapse in={accordionState[index]}>
                  {drawer.type === 'Taxes' && taxData && renderTaxes(taxData)}
                  {drawer.type &&
                    ['ExteriorFeatures', 'Construction', 'Parking', 'Utilities'].includes(
                      drawer.type,
                    ) &&
                    renderFourColumnDrawer(drawer)}
                  {drawer.sections && renderSections(drawer.sections)}
                  {drawer.header === 'Inclusions' && drawer.list && (
                    <p data-testid="inclusions-content" key={drawer.list[0]}>
                      {drawer.list[0]}
                    </p>
                  )}
                  {drawer.header === 'Exclusions' && drawer.list && (
                    <p data-testid="exclusions-content" key={drawer.list[0]}>
                      {drawer.list[0]}
                    </p>
                  )}
                  {drawer.header === 'Addenda' || drawer.header === 'Addendum' ? (
                    <li
                      style={{ color: '#757575' }}
                      data-testid="addenda-content"
                      key={index.toString()}
                      dangerouslySetInnerHTML={{
                        __html: drawer?.list?.length ? drawer.list[0]! : '',
                      }}
                    />
                  ) : (
                    drawer.list &&
                    drawer.list.length > 0 && (
                      <SectionList>
                        {drawer.list.map((item, index) => (
                          <li
                            data-testid={`${getTestId(drawer.header)}-item-${index}`}
                            key={index.toString()}
                          >
                            {item && item}
                          </li>
                        ))}
                      </SectionList>
                    )
                  )}
                  {drawer.description && (
                    <p data-testid="drawer-description">{drawer.description}</p>
                  )}
                  {drawer.type === 'AdditionalTerms' && renderAdditionalTerms(drawer)}
                </Collapse>
              )}
            </ListItem>
          ))}
        </List>
      </Drawers>
    </Block>
  );
};

export default PropertyDetailDrawer;
